import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { RootState, AppDispatch, PayloadMap, getClient } from '@src/core/store';
import { ParameterInfo } from '@src/core/hooks';
import {
  Product,
  Additional,
  AddCartItemRequest,
  AddCartItemTariff,
  Scheduling,
  TariffGroup,
  TariffType,
  ScheduleTime,
  SeatMapOverview,
  SeatMapSlotResource,
  SellingMode,
  AdditionalType,
  ProductPackage,
  ProductPackageProduct,
  AddProductPackageToCartRequest,
} from '@src/core/buyerApiClient';
import { addToCart } from '@src/features/cart';
import { getSchedulingFrom, SeatMapAllocationAttempt } from '@src/common/seatMaps';
import {
  mountTariffsPerTypeListFromTariffGroup,
  mountTariffsPerTypeListFromSchedulings,
  getAdditionalPrice,
  getSelectedTariff,
  getTariffPriceInformation,
  isTariffQtdSelectedAvailable,
  tariffDepedencyStatus,
} from '@src/common/tariffs';
import { schedulingHasAvailableOccupation } from '@src/common/schedulings';
import { getPaginationTotalCount } from '@src/common';
import { track } from '@src/services/analytics';
import { getAdditionalAvailability, validateAdditionalQuantity } from '@src/common/additionals';

export interface ProductState {
  products: { [key: string]: FullProduct };
  packages: { [key: string]: PackageState };
  loadingProducts: { [key: string]: boolean };
  loadingSchedulingSeatMap: { [key: string]: boolean };
  productSchedules: { [key: string]: ProductScheduleState };
  seatMaps: { [key: string]: SeatMapOverview | undefined };
  adderState: { [key: string]: ProductAdderState };
  highlightedProductAdder?: HighlightedProductAdder;
}

export type ProductPackageStatus = 'idle' | 'loading' | 'loaded' | 'loadingNextProduct' | 'completed';
export interface PackageState {
  productPackage?: ProductPackage;
  productPackageProducts?: ProductPackageProduct[];
  error?: string;
  status?: ProductPackageStatus;
  currentProductIndexAtAdder?: number;
}

export type FullProduct = Product & {
  additionals: Array<Additional>;
};

export interface ProductScheduleState {
  loadedKeys: { [key: string]: boolean };
  dates: {
    [key: string]: {
      availables: Scheduling[];
      unavailables: Scheduling[];
    };
  };
  defaultTariffGroup?: TariffGroup;
  pending: boolean;
}

type CurrentProductAdderStatus = 'ongoing' | 'adding_to_cart' | 'finished_request';

export interface ProductAdderState {
  productId: string;
  packageId?: string;
  currentStatus: CurrentProductAdderStatus;
  addingToCart: boolean;
  autoSelectProducts?: boolean;
  isClosingAdderModal?: boolean;
  productSelectionState: Record<string, ProductSelectionState>;
}

interface ProductSelectionState {
  currentProductAdderStep?: ProductStepType;
  tariffsState: TariffsInformation;
  selectedTariffHour?: ScheduleTime;
  productAdditionals: { [key: string]: ProductAdditionalState };
  selectedResources: SelectedResource[];
  resourcesAreOk: boolean;
  exceedingValueCents: number;
  seatMapAllocationStatus: SeatMapAllocationAttempt;
}

interface AddToCartPayloadProps {
  productId: string;
  selectedTariffs: TariffsPerType[];
  selectedDate?: string;
  selectedTariffHour?: ScheduleTime;
  selectedAdditionals: ProductAdditionalState[];
  selectedResources: SelectedResource[];
}

export interface SelectedResource {
  resourceSlot: SeatMapSlotResource;
  tariffs: SelectedResouceTariff[];
}

export interface SelectedResouceTariff {
  tariffId: string;
  quantity: number;
}

export interface TariffsInformation {
  selectedDate?: string;
  listOfTariffsPerType: TariffsPerType[];
}

export interface TariffsPerType {
  type: TariffType;
  selectedQuantity: number;
  schedulings: Scheduling[];
  tariffGroup?: TariffGroup;
}

export interface ProductAdditionalState {
  checked: boolean;
  input: string;
  additional: Additional;
  quantity?: number;
}

export interface HighlightedProductAdder {
  id: string;
  params: ParameterInfo;
}

const productSteps = ['calendar', 'tariff', 'seat_map', 'additional', 'finish'] as const;
export type ProductStepType = (typeof productSteps)[number];

const initialProductSelectionState = {
  productAdditionals: {},
  selectedResources: [],
  resourcesAreOk: false,
  exceedingValueCents: 0,
  tariffsState: { listOfTariffsPerType: [] },
  seatMapAllocationStatus: { success: true, missingCapacity: 0, missingTariffs: 0 },
};

const initialProductAdderState = (productId: string, packageId?: string): ProductAdderState => ({
  productId,
  packageId,
  addingToCart: false,
  autoSelectProducts: false,
  isClosingAdderModal: false,
  currentStatus: 'ongoing',
  productSelectionState: {
    [productId]: initialProductSelectionState,
  },
});

const initialState: ProductState = {
  products: {},
  loadingProducts: {},
  loadingSchedulingSeatMap: {},
  productSchedules: {},
  adderState: {},
  seatMaps: {},
  packages: {},
};

const productLoadTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};

export const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    setProduct: (state: ProductState, { payload: prod }: PayloadAction<FullProduct>) => {
      state.products[prod.id] = prod;
    },
    setProductLoad: {
      reducer(state, { payload, meta: { productId } }: PayloadAction<boolean, string, { productId: string }>) {
        state.loadingProducts[productId] = payload;
      },
      prepare(productId: string, loading: boolean) {
        return { payload: loading, meta: { productId } };
      },
    },
    setSeatMapLoading: {
      reducer(state, { payload, meta: { schedulingId } }: PayloadAction<boolean, string, { schedulingId: string }>) {
        state.loadingSchedulingSeatMap[schedulingId] = payload;
      },
      prepare(schedulingId: string, loading: boolean) {
        return { payload: loading, meta: { schedulingId } };
      },
    },
    initializeAdderState: {
      reducer(
        state,
        {
          payload,
          meta: { productId, packageId },
        }: PayloadAction<string, string, { productId: string; packageId: string }>,
      ) {
        state.adderState[payload] = initialProductAdderState(productId, packageId);
      },
      prepare(key: string, productId: string, packageId: string) {
        return { payload: key, meta: { productId, packageId } };
      },
    },
    updateAdderStateProductId: (state: ProductState, { payload }: PayloadAction<PayloadMap<string>>) => {
      const adderState = state.adderState[payload.id];

      if (adderState) {
        adderState.productId = payload.value;
      }
    },
    updateAdderStateProductSelectionState: (
      state: ProductState,
      { payload }: PayloadAction<PayloadMap<Record<string, ProductSelectionState>>>,
    ) => {
      const adderState = state.adderState[payload.id];

      if (adderState) {
        adderState.productSelectionState = payload.value;
      }
    },
    requestAvailableDates: (state: ProductState, { payload: id }: PayloadAction<string, string>) => {
      if (state.productSchedules[id]) {
        state.productSchedules[id]!.pending = true;
      } else {
        state.productSchedules[id] = {
          loadedKeys: {},
          dates: {},
          pending: true,
        };
      }
    },
    receiveAvailableDates: {
      reducer(
        state,
        {
          payload: schedulings,
          meta: { productId, key },
        }: PayloadAction<Scheduling[], string, { productId: string; key: string }>,
      ) {
        const productSchedules = state.productSchedules[productId];
        if (productSchedules === undefined) {
          return;
        }

        productSchedules.loadedKeys[key] = true;
        productSchedules.pending = false;

        const dates = schedulings.reduce((carry, sch) => {
          const isAvailable = schedulingHasAvailableOccupation(sch);

          const availables = carry?.[sch.date]?.availables ?? [];
          const unavailables = carry?.[sch.date]?.unavailables ?? [];

          return {
            ...carry,
            [sch.date]: {
              availables: isAvailable ? [...availables, sch] : availables,
              unavailables: isAvailable ? unavailables : [...unavailables, sch],
            },
          };
        }, {} as ProductScheduleState['dates']);

        for (const key in dates) {
          productSchedules.dates![key] = dates[key]!;
        }
      },
      prepare(productId: string, key: string, payload: Scheduling[]) {
        return { payload, meta: { productId, key } };
      },
    },
    requestTariffGroup: (state: ProductState, { payload: id }: PayloadAction<string, string>) => {
      if (state.productSchedules[id]) {
        state.productSchedules[id]!.pending = true;
      } else {
        state.productSchedules[id] = {
          loadedKeys: {},
          dates: {},
          pending: true,
        };
      }
    },
    receiveTariffGroup: {
      reducer(
        state,
        {
          payload: tariffGroup,
          meta: { productId, key },
        }: PayloadAction<TariffGroup, string, { productId: string; key: string }>,
      ) {
        const productSchedules = state.productSchedules[productId];
        if (productSchedules === undefined) {
          return;
        }

        productSchedules.loadedKeys[key] = true;
        productSchedules.pending = false;
        productSchedules.defaultTariffGroup = tariffGroup;
      },
      prepare(productId: string, key: string, payload: TariffGroup) {
        return { payload, meta: { productId, key } };
      },
    },
    finishRequest: (state: ProductState, { payload: id }: PayloadAction<string, string>) => {
      state.productSchedules[id]!.pending = false;
    },
    updateProductAvailableDateTariff: (state: ProductState, action: PayloadAction<PayloadMap<TariffsPerType>>) => {
      const id = action.payload.id;
      const tariffPerType = action.payload.value;
      const typeId = tariffPerType.type.id;
      const newTariffPerType = action.payload.value;
      const adderState = state.adderState[id];

      if (adderState) {
        const productId = adderState.productId;
        const tariffsState = adderState.productSelectionState[productId]!.tariffsState;

        tariffsState.listOfTariffsPerType = tariffsState.listOfTariffsPerType.map((tariff) => {
          return tariff.type.id === typeId ? newTariffPerType : tariff;
        });
      }
    },
    selectAvailableDate: (state: ProductState, action: PayloadAction<PayloadMap<string | null>>) => {
      const adderState = state.adderState[action.payload.id];
      if (adderState == null) {
        return;
      }

      const { productId } = adderState!;
      const tariffsState = adderState.productSelectionState[productId]!.tariffsState;

      const productSchedules = state.productSchedules[productId];
      const product = state.products[productId];
      const minSaleTariffQuantities = product?.minSaleTariffQuantity ?? 0;

      const { value: selectedDate } = action.payload;
      if (tariffsState && productSchedules) {
        tariffsState.selectedDate = selectedDate ?? undefined;

        if (selectedDate === null) {
          if (productSchedules.defaultTariffGroup) {
            tariffsState.listOfTariffsPerType = mountTariffsPerTypeListFromTariffGroup(
              productSchedules.defaultTariffGroup,
              minSaleTariffQuantities,
            );
          }
        } else {
          const schedulings = productSchedules.dates[selectedDate];
          if (schedulings) {
            tariffsState.listOfTariffsPerType = mountTariffsPerTypeListFromSchedulings(
              schedulings.availables,
              minSaleTariffQuantities,
            );
          }
        }
      }

      adderState.productSelectionState[productId]!.selectedTariffHour = undefined;
    },
    changeProductAddStep: (state: ProductState, { payload }: PayloadAction<PayloadMap<ProductStepType>>) => {
      const adderState = state.adderState[payload.id]!;
      const productId = adderState.productId;

      adderState.productSelectionState[productId]!.currentProductAdderStep = payload.value;
    },
    changeSelectedHour: (state: ProductState, action: PayloadAction<PayloadMap<ScheduleTime | undefined>>) => {
      const adderState = state.adderState[action.payload.id]!;
      const productId = adderState.productId;

      adderState.productSelectionState[productId]!.selectedTariffHour = action.payload.value;
    },
    updateProductAdditional: (state: ProductState, { payload }: PayloadAction<PayloadMap<ProductAdditionalState>>) => {
      const adderState = state.adderState[payload.id]!;
      const productId = adderState.productId;

      adderState.productSelectionState[productId]!.productAdditionals[payload.value.additional.id] = payload.value;
    },
    setSelectedResources: {
      reducer(
        state,
        {
          payload,
          meta: { key, isOk, exceedingValueCents },
        }: PayloadAction<SelectedResource[], string, { key: string; isOk: boolean; exceedingValueCents: number }>,
      ) {
        const adderState = state.adderState[key]!;
        const productId = adderState.productId;

        adderState.productSelectionState[productId]!.selectedResources = payload;
        adderState.productSelectionState[productId]!.resourcesAreOk = isOk;
        adderState.productSelectionState[productId]!.exceedingValueCents = exceedingValueCents;
      },
      prepare(key: string, payload: SelectedResource[], isOk: boolean, exceedingValueCents: number) {
        return { payload, meta: { key, isOk, exceedingValueCents } };
      },
    },
    setSeatMapAllocationStatus: {
      reducer(state, { payload, meta: { key } }: PayloadAction<SeatMapAllocationAttempt, string, { key: string }>) {
        const adderState = state.adderState[key]!;
        const productId = adderState.productId;

        adderState!.productSelectionState[productId]!.seatMapAllocationStatus = payload;
      },
      prepare(key: string, payload: SeatMapAllocationAttempt) {
        return { payload, meta: { key } };
      },
    },
    setProductAdderStatus: (state: ProductState, { payload }: PayloadAction<PayloadMap<CurrentProductAdderStatus>>) => {
      const adderState = state.adderState[payload.id]!;
      const productId = adderState.productId;

      adderState.currentStatus = payload.value;

      if (payload.value === 'finished_request') {
        adderState.productSelectionState[productId]!.currentProductAdderStep = 'finish';
      }
    },
    setSeatMap: {
      reducer(
        state,
        {
          payload: seatMap,
          meta: { schedulingId, timeMinutes },
        }: PayloadAction<SeatMapOverview | undefined, string, { schedulingId: string; timeMinutes?: number }>,
      ) {
        const key = formatSeatMapKey(schedulingId, timeMinutes);
        state.seatMaps[key] = seatMap;
      },
      prepare(schedulingId: string, timeMinutes?: number, payload?: SeatMapOverview) {
        return { payload, meta: { schedulingId, timeMinutes } };
      },
    },
    setHighlightedProductAdder: {
      reducer(
        state,
        { payload: params, meta: { widgetId: id } }: PayloadAction<ParameterInfo, string, { widgetId: string }>,
      ) {
        state.highlightedProductAdder = { id, params };
      },
      prepare(widgetId: string, params: ParameterInfo) {
        return { payload: params, meta: { widgetId } };
      },
    },
    clearHighlightedProductAdder: (state: ProductState) => {
      state.highlightedProductAdder = undefined;
    },
    setPackage: {
      reducer(state, { payload: productPackageState, meta: packageId }: PayloadAction<PackageState, string, string>) {
        state.packages[packageId] = productPackageState;
      },
      prepare(packageId: string, packageState: PackageState) {
        return { payload: packageState, meta: packageId };
      },
    },
    updateIsClosingAdderModal: {
      reducer(state, { payload: id, meta: value }: PayloadAction<string, string, boolean>) {
        state.adderState[id]!.isClosingAdderModal = value;
      },
      prepare(id: string, value: boolean) {
        return { payload: id, meta: value };
      },
    },
    updateAdderStateAutoSelectProducts: (state: ProductState, { payload }: PayloadAction<PayloadMap<boolean>>) => {
      const adderState = state.adderState[payload.id];

      if (adderState) {
        adderState.autoSelectProducts = payload.value;
      }
    },
  },
});

export default productSlice.reducer;

export const {
  setProduct,
  setProductLoad,
  requestTariffGroup,
  receiveTariffGroup,
  requestAvailableDates,
  receiveAvailableDates,
  finishRequest,
  setSeatMap,
  initializeAdderState,
  changeProductAddStep,
  selectAvailableDate,
  updateProductAvailableDateTariff,
  setProductAdderStatus,
  setHighlightedProductAdder,
  clearHighlightedProductAdder,
  changeSelectedHour,
  setSelectedResources,
  setSeatMapAllocationStatus,
  setSeatMapLoading,
  setPackage,
  updateIsClosingAdderModal,
  updateAdderStateAutoSelectProducts,
  updateAdderStateProductSelectionState,
  updateAdderStateProductId,
} = productSlice.actions;

export const loadProduct = (id: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  if (state.product.products[id] || state.product.loadingProducts[id]) {
    return;
  }

  dispatch(setProductLoad(id, true));

  const client = getClient(state);
  try {
    const { data: product } = await client.products.getProduct(id);
    dispatch(setProduct({ ...product, additionals: [] }));

    const pageSize = 100;
    const additionals = [];

    for (let offset = 0, total = pageSize; offset < total; offset += pageSize) {
      const response = await client.products.getProductAdditionals(id, offset, pageSize);

      additionals.push(...response.data);
      total = getPaginationTotalCount(response);
    }

    dispatch(setProduct({ ...product, additionals }));

    if (product.sellingMode === SellingMode.WithoutDate) {
      dispatch(loadDefaultTariffGroup(id));
    }
  } finally {
    dispatch(setProductLoad(id, false));
  }
};

const loadProductAndInitializeAdderState =
  (id: string, productId: string, packageId = '') =>
  async (dispatch: AppDispatch) => {
    if (productLoadTimeouts[id]) {
      clearTimeout(productLoadTimeouts[id]);
    }

    productLoadTimeouts[id] = setTimeout(() => {
      dispatch(loadProduct(productId));
    }, 100);

    dispatch(initializeAdderState(id, productId, packageId));
  };

export const loadAdderState =
  (id: string, productId?: string, packageId = '', shouldSelectFirstProductAutomatically?: boolean) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const adderState = getState().product.adderState[id];

    if (!adderState) {
      if (productId) {
        dispatch(loadProductAndInitializeAdderState(id, productId, packageId));
        return;
      }

      if (packageId) {
        dispatch(setupProductPackage(id, packageId, shouldSelectFirstProductAutomatically));
      }
    }
  };

export const resetAdderState =
  (id: string, productId: string, packageId = '') =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { highlightedProductAdder: hpa } = getState().product;

    if (packageId) {
      dispatch(resetAdderStateWithPackage(id, packageId));
    } else {
      dispatch(initializeAdderState(id, productId, packageId));
    }

    if (hpa) {
      dispatch(setHighlightedProductAdder(hpa.id, hpa.params));
    }
  };

const adderState = (id: string) => (state: RootState) => {
  return state.product.adderState[id] || initialProductAdderState('');
};

export const updateProductAdditional = (id: string, state: ProductAdditionalState) => (dispatch: AppDispatch) => {
  dispatch(productSlice.actions.updateProductAdditional({ id, value: state }));
};

export const loadDefaultTariffGroup =
  (productId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      product: { productSchedules },
    } = state;

    const key = 'defaultTariffGroup';
    if (productSchedules[productId] && productSchedules[productId]!.loadedKeys[key]) {
      return;
    }

    dispatch(requestTariffGroup(productId));

    let response: AxiosResponse<TariffGroup>;
    try {
      response = await getClient(state).products.getProductDefaultTariffGroup(productId);
    } finally {
      dispatch(finishRequest(productId));
    }

    const { data: tariffGroup } = response;
    dispatch(receiveTariffGroup(productId, key, tariffGroup));
  };

export const loadAvailableDates =
  (productId: string, fromDate: string, toDate: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      product: { productSchedules },
    } = state;

    const key = [fromDate, toDate].join('-');
    if (productSchedules[productId] && productSchedules[productId]!.loadedKeys[key]) {
      return;
    }

    dispatch(requestAvailableDates(productId));

    const client = getClient(state);
    const schedulings: Scheduling[] = [];
    const pageSize = 100;

    try {
      for (let offset = 0, total = pageSize; offset < total; offset += pageSize) {
        const resp = await client.products.getProductSchedulings(productId, fromDate, toDate, offset, pageSize);

        schedulings.push(...resp.data);
        total = getPaginationTotalCount(resp);
      }
    } finally {
      dispatch(finishRequest(productId));
    }

    dispatch(receiveAvailableDates(productId, key, schedulings));
  };

const setStepIndexAddStep = (id: string, idx: number) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const steps = makeSteps(id, getState());
  dispatch(changeProductAddStep({ id, value: steps[idx]! }));
};

export const resetSteps = (id: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const steps = makeSteps(id, getState());

  if (steps.length) {
    dispatch(setStepIndexAddStep(id, 0));
  }
};

export const moveProductAddStep =
  (id: string, dir: number) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const { steps, currentStep } = productAdderStepState(id)(getState());
    const index = steps.indexOf(currentStep!);

    dispatch(setStepIndexAddStep(id, index + dir));
  };

export const previousProductAddStep = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(moveProductAddStep(id, -1));
};

const nextProductAddStep = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(moveProductAddStep(id, 1));
};

export const goToNextStep = (id: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  const { isAtLast } = productAdderStepState(id)(state);

  if (!isAtLast) {
    dispatch(nextProductAddStep(id));
    return;
  }

  if (isProductPackage(id)(state)) {
    dispatch(goToNextProductInThePackage(id));

    return;
  }

  dispatch(addProductToCart(id));
};

const addProductToCart = (id: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  const adderState = state.product.adderState[id]!;
  const productId = adderState.productId;

  const { selectedTariffHour } = adderState.productSelectionState[productId]!;
  const { selectedDate } = tariffsState(id)(state);
  const { selectedTariffs, selectedAdditionals, selectedResources } = adderResumeSelector(id)(state);

  const params = createAddToCartItemRequestPayload({
    productId,
    selectedTariffs,
    selectedDate,
    selectedTariffHour,
    selectedAdditionals,
    selectedResources,
  }) as AddCartItemRequest;

  const product = productSelector(productId)(state);

  track('productAdderProductAdded', {
    productId: product!.id,
    productName: product!.name,
    date: params.scheduleDate,
    time: params.scheduleTimeMinutes,
    additionals: params.additionals?.length ?? 0,
    resources: params.resources?.length ?? 0,
    tariffs: params.tariffs.reduce((acc, tariff) => acc + tariff.quantity, 0),
  });

  dispatch(addToCart(params, id));
};

const createAddToCartItemRequestPayload = (props: AddToCartPayloadProps) => {
  const { productId, selectedAdditionals, selectedResources, selectedTariffs, selectedDate, selectedTariffHour } =
    props;

  const minutes = selectedTariffHour?.minutes ?? null;
  const reqTariffs: AddCartItemTariff[] = [];

  for (const tariffType of selectedTariffs) {
    const tariff = getSelectedTariff(tariffType, minutes);
    if (tariff == null) {
      return;
    }

    reqTariffs.push({
      tariffId: tariff.id,
      quantity: tariffType.selectedQuantity,
    });
  }

  const params: AddCartItemRequest = {
    productId,
    scheduleDate: selectedDate ?? null,
    scheduleTimeMinutes: minutes,
    tariffs: reqTariffs,
    additionals: selectedAdditionals.map((add) => ({
      additionalId: add.additional.id,
      inputValue: add.input,
      quantity: add.quantity,
    })),
    resources: [],
  };

  if (selectedResources.length) {
    params.resources = selectedResources.map((sr) => ({
      resourceId: sr.resourceSlot.id,
      tariffs: sr.tariffs.map((srt) => ({
        tariffId: srt.tariffId,
        quantity: srt.quantity,
      })),
    }));
  }

  return params;
};

export const isCurrentStepValid =
  (id: string) =>
  (state: RootState): boolean => {
    const adderState = state.product.adderState[id]!;
    const { currentStep } = productAdderStepState(id)(state);
    const { dates } = productScheduleSelector(adderState.productId)(state);

    const productId = adderState.productId;
    const currentProduct = adderState.productSelectionState[productId]!;

    switch (currentStep) {
      case 'calendar':
        return !!currentProduct.tariffsState.selectedDate;
      case 'tariff':
        const {
          tariffsState: { listOfTariffsPerType },
        } = currentProduct;
        const selectedHour = currentProduct.selectedTariffHour;
        const product = state.product.products[productId]!;
        const hasSelectedATariff =
          listOfTariffsPerType.flatMap((tariff) => tariff.selectedQuantity).filter((quantity) => quantity).length > 0;
        const hourIsValid = product?.sellingMode != SellingMode.WithDateAndTime || selectedHour;
        const isQtdSelectedValid =
          product.sellingMode != SellingMode.WithDateOnly || isTariffQtdSelectedAvailable(listOfTariffsPerType);
        const hasNotTariffDependencyError = !tariffDepedencyStatus(listOfTariffsPerType, selectedHour)
          .hasTariffDependencyError;
        const isSeatMapLoading = Object.values(state.product.loadingSchedulingSeatMap).some((loading) => loading);

        const allocationStatus = currentProduct.seatMapAllocationStatus;

        return !!(
          hasSelectedATariff &&
          hourIsValid &&
          isQtdSelectedValid &&
          hasNotTariffDependencyError &&
          allocationStatus.success &&
          !isSeatMapLoading
        );
      case 'seat_map':
        return currentProduct.resourcesAreOk;
      case 'additional': {
        const productAddditionals = currentProduct.productAdditionals;
        const additionalsCheckedWithInputEnabled = Object.values(productAddditionals).filter(
          (s) => s.checked && s.additional.enableInput,
        );

        const additionalsCheckedWithCustomQuantity = Object.values(productAddditionals).filter(
          (s) => s.checked && s.additional.type === AdditionalType.Quantity,
        );

        const additionalsAvailabilityAmount: Record<string, number | null> =
          additionalsCheckedWithCustomQuantity.reduce((acc, cur) => {
            return {
              ...acc,
              [cur.additional.id]: getAdditionalAvailability(
                dates[currentProduct.tariffsState.selectedDate ?? '']?.availables,
                cur.additional,
                state.product.products[productId]!,
                currentProduct.tariffsState.selectedDate,
                currentProduct.selectedTariffHour?.minutes,
                currentProduct.tariffsState.listOfTariffsPerType,
              ).availableAmount,
            };
          }, {});

        const isAdditionalQuantityValid =
          additionalsCheckedWithCustomQuantity.length === 0 ||
          additionalsCheckedWithCustomQuantity.every(
            (s) =>
              validateAdditionalQuantity({
                availableAmount: additionalsAvailabilityAmount[s.additional.id] ?? null,
                additional: s.additional,
                quantity: s.quantity,
              }).isQuantityValid,
          );

        const isInputEnabledValid = additionalsCheckedWithInputEnabled.filter((s) => !s.input.trim()).length == 0;

        return isInputEnabledValid && isAdditionalQuantityValid;
      }
      default:
        return true;
    }
  };

export const loadSeatMap =
  (scheduling: Scheduling, timeMinutes?: number) => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setSeatMap(scheduling.id, timeMinutes, undefined));
      dispatch(setSeatMapLoading(scheduling.id, true));

      const { data: seatMap } = await getClient(getState()).seatMaps.getSchedulingSeatMapOverview(
        scheduling.id,
        timeMinutes,
      );

      dispatch(setSeatMap(scheduling.id, timeMinutes, seatMap));
    } finally {
      dispatch(setSeatMapLoading(scheduling.id, false));
    }
  };

export const formatSeatMapKey = (schedulingId: string, timeMinutes?: number) => `${schedulingId}-${timeMinutes}`;

export const productSelector = (id: string) => (state: RootState) => state.product.products[id];

export const additionalsSelector = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  return adderState.productSelectionState[resolvedProductId]?.productAdditionals;
};

export const productScheduleSelector =
  (id: string) =>
  (state: RootState): ProductScheduleState =>
    state.product.productSchedules[id] || {
      dates: {},
      loadedKeys: {},
      pending: true,
    };

export const schedulingSeatMapSelector =
  (schedulingId: string, timeMinutes?: number) =>
  (state: RootState): SeatMapOverview | undefined =>
    state.product.seatMaps[formatSeatMapKey(schedulingId, timeMinutes)];

export const seatMapSelector = () => (state: RootState) => state.product.seatMaps;

export const seatMapAllocationStatusSelector = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  return adderState.productSelectionState[resolvedProductId]!.seatMapAllocationStatus;
};

export const schedulingSeatMapLoadingSelector = (schedulingId: string) => (state: RootState) =>
  state.product.loadingSchedulingSeatMap[schedulingId];

const makeSteps = (widgetId: string, state: RootState): ProductStepType[] => {
  const addState = adderState(widgetId)(state);
  const product = productSelector(addState.productId)(state);

  if (!product) {
    return [];
  }

  const isWithoutDate = product!.sellingMode === SellingMode.WithoutDate;
  const steps: ProductStepType[] = [];
  if (!isWithoutDate) {
    steps.push('calendar');
  }

  steps.push('tariff');

  const { listOfTariffsPerType } = tariffsState(widgetId)(state);
  const selectedHour = selectedProductTariffHour(widgetId)(state);
  const selectedMinutes = selectedHour?.minutes ?? null;
  const selectedScheduling = getSchedulingFrom(product!, listOfTariffsPerType, selectedMinutes);

  if (!isWithoutDate && selectedScheduling?.requiresSeatSelection) {
    steps.push('seat_map');
  }

  if (product.additionals.length) {
    steps.push('additional');
  }

  steps.push('finish');

  return steps;
};

export const productAdderStepState = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  if (!resolvedProductId) {
    return { steps: [], currentStep: undefined, isAtLast: false };
  }

  let { currentProductAdderStep: currentStep } = adderState.productSelectionState[resolvedProductId]!;

  const steps = makeSteps(id, state);

  if (currentStep == null && steps.length) {
    currentStep = steps[0];
  }

  const validSteps = steps.filter((s) => s !== 'finish');
  const isAtLast = currentStep == validSteps[validSteps.length - 1];

  return { steps, currentStep, isAtLast };
};

export const tariffsState =
  (id: string, productId?: string) =>
  (state: RootState): TariffsState => {
    const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
    const resolvedProductId = productId ?? currentProductId;

    if (!resolvedProductId) {
      return { listOfTariffsPerType: [], selectedDate: undefined };
    }

    const tariffsState = adderState.productSelectionState[resolvedProductId]!.tariffsState;
    const selectedDate = tariffsState.selectedDate;
    const listOfTariffsPerType = tariffsState.listOfTariffsPerType;

    return { listOfTariffsPerType, selectedDate };
  };

export interface TariffsState {
  selectedDate?: string;
  listOfTariffsPerType: TariffsPerType[];
}

export const selectedProductTariffHour = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  return adderState.productSelectionState[resolvedProductId]?.selectedTariffHour;
};

export const selectedTariffsSelector = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  if (!resolvedProductId) {
    return [];
  }

  return adderState.productSelectionState[resolvedProductId]!.tariffsState.listOfTariffsPerType.filter(
    (tariff) => tariff.selectedQuantity,
  );
};

export const selectedProductResources = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  return adderState.productSelectionState[resolvedProductId]!.selectedResources;
};

export const selectedAdditionalsSelector = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  if (!resolvedProductId) {
    return [];
  }

  const productAdditionals = adderState.productSelectionState[resolvedProductId]!.productAdditionals;

  return Object.values(productAdditionals).filter((s) => s.checked);
};

export const currentProductAdderStatus = (id: string) => (state: RootState) => adderState(id)(state).currentStatus;

export const getExceedingValueCents = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  return adderState.productSelectionState[resolvedProductId]!.exceedingValueCents;
};

export const productAdditionalFormInput =
  (widgetId: string, additional: Additional, productId?: string) => (state: RootState) => {
    const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(widgetId)(state);
    const resolvedProductId = productId ?? currentProductId;

    const stateAdditional = adderState.productSelectionState[resolvedProductId]!.productAdditionals[additional.id];

    return stateAdditional ?? { additional, input: '', checked: false, quantity: undefined };
  };

export const adderResumeSelector = (id: string, prodId?: string) => (state: RootState) => {
  const { adderState: widgetAdderState, productId: currentProductId } =
    adderStateAndCurrentProductIdSelector(id)(state);

  const productId = prodId ?? currentProductId;

  if (!productId)
    return {
      selectedTariffHour: undefined,
      productId: '',
      selectedTariffs: [],
      selectedDate: undefined,
      tariffsPriceCents: 0,
      selectedAdditionals: [],
      additionalsPriceCents: 0,
      selectedResources: [],
      resourcesPriceCents: 0,
      totalCents: 0,
    };

  const selectedTariffs = selectedTariffsSelector(id, productId)(state);
  const selectedAdditionals = selectedAdditionalsSelector(id, productId)(state);
  const productSelectionState = widgetAdderState.productSelectionState;

  const {
    selectedTariffHour,
    tariffsState: { listOfTariffsPerType, selectedDate },
    exceedingValueCents,
    selectedResources,
  } = productSelectionState[productId]!;

  const tariffsResume = listOfTariffsPerType
    .flatMap((t) => t)
    .reduce(
      ({ price, quantity }, tariffsPerType) => {
        price += getTariffPriceInformation(tariffsPerType, selectedTariffHour ?? null);
        quantity += tariffsPerType.selectedQuantity;

        return { price, quantity };
      },
      { price: 0, quantity: 0 },
    );

  const additionalsPriceCents = selectedAdditionals.reduce(
    (sum, s) => sum + getAdditionalPrice(s.additional, listOfTariffsPerType, s.quantity),
    0,
  );

  return {
    selectedTariffHour,
    productId,
    selectedTariffs,
    selectedDate,
    tariffsPriceCents: tariffsResume.price,
    selectedAdditionals,
    additionalsPriceCents,
    selectedResources,
    resourcesPriceCents: exceedingValueCents,
    totalCents: tariffsResume.price + exceedingValueCents + additionalsPriceCents,
  };
};

export const highlightedProductAdderSelector = (state: RootState) => state.product.highlightedProductAdder;

export const loadingProductSelector = (id: string) => (state: RootState) => state.product.loadingProducts[id];

export const productAdderSelectedProduct = (id: string) => (state: RootState) => adderState(id)(state).productId;

export const packageSelector = (packageId: string) => (state: RootState) => state.product.packages[packageId];

export const updatePackage =
  (packageId: string, packageState: PackageState) => (dispatch: AppDispatch, getState: () => RootState) => {
    const currentPackageState = packageSelector(packageId)(getState());

    if (currentPackageState) {
      dispatch(setPackage(packageId, { ...currentPackageState, ...packageState }));
    } else {
      dispatch(setPackage(packageId, packageState));
    }
  };

const resetAdderStateWithPackage =
  (widgetId: string, packageId: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const productPackageProducts = productPackageProductsSelector(packageId)(state)!;
    const autoSelectProducts = adderState(widgetId)(state).autoSelectProducts;

    if (productPackageProducts.length) {
      dispatch(startAdderStateWithPackage(widgetId, packageId, productPackageProducts, autoSelectProducts));
    }

    dispatch(updatePackage(packageId, { status: 'loaded' }));
  };

const startAdderStateWithPackage =
  (widgetId: string, packageId: string, productPackageProducts: ProductPackageProduct[], shouldAutoSelect?: boolean) =>
  async (dispatch: AppDispatch) => {
    const productId = shouldAutoSelect ? productPackageProducts[0]!.productId : '';
    const productSelectionState = productPackageProducts.reduce((acc, ppp) => {
      acc[ppp.productId] = {
        ...initialProductSelectionState,
      };
      return acc;
    }, {} as Record<string, ProductSelectionState>);

    dispatch(loadProductAndInitializeAdderState(widgetId, productId, packageId));

    if (shouldAutoSelect) {
      dispatch(updatePackage(packageId, { currentProductIndexAtAdder: 0 }));
      dispatch(updateAdderStateAutoSelectProducts({ id: widgetId, value: true }));
    }

    dispatch(updateAdderStateProductSelectionState({ id: widgetId, value: productSelectionState }));
  };

export const setupProductPackage =
  (widgetId: string, packageId: string, shouldSelectFirstProductAutomatically?: boolean) =>
  async (dispatch: AppDispatch) => {
    dispatch(updatePackage(packageId, { status: 'loading' }));

    await Promise.all([
      dispatch(loadProductPackage(packageId)),
      dispatch(loadProductPackageProducts(widgetId, packageId, shouldSelectFirstProductAutomatically)),
    ]);
  };

const triggerError = (packageId: string, error: string) => (dispatch: AppDispatch) => {
  dispatch(updatePackage(packageId, { error }));
};

export const loadProductPackage = (packageId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  try {
    const productPackage = await fetchProductPackage(packageId, getState());

    dispatch(updatePackage(packageId, { productPackage }));
  } catch (error: any) {
    dispatch(triggerError(packageId, 'product_package_error_while_load_package_data'));
  }
};

const fetchProductPackage = async (packageId: string, rootState: RootState) => {
  const { data } = await getClient(rootState).productPackage.getProductPackage(packageId);

  return data;
};

export const loadProductPackageProducts =
  (widgetId: string, packageId: string, shouldAutoSelect?: boolean) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    try {
      const productPackageProducts = await fetchProductPackageProducts(packageId, state);

      if (isProductPackageProductsEmpty(productPackageProducts)) {
        dispatch(triggerError(packageId, 'product_package_error_empty_package'));
        return;
      }

      dispatch(updatePackage(packageId, { productPackageProducts }));

      const hasInactiveProduct = checkIfThePackageHasInactiveProductSelector(packageId)(state);

      if (hasInactiveProduct) {
        dispatch(triggerError(packageId, 'product_package_error_has_inactive_product'));

        return;
      }

      dispatch(startAdderStateWithPackage(widgetId, packageId, productPackageProducts, shouldAutoSelect));
    } catch (error: any) {
      dispatch(triggerError(packageId, 'product_package_error_while_load_package_products_data'));
    } finally {
      dispatch(updatePackage(packageId, { status: 'loaded' }));
    }
  };

const fetchProductPackageProducts = async (packageId: string, rootState: RootState) => {
  const { data } = await getClient(rootState).productPackage.getProductPackageProducts(packageId);

  return data;
};

const isProductPackageProductsEmpty = (productPackageProducts: ProductPackageProduct[]) =>
  !productPackageProducts || productPackageProducts.length === 0;

export const goToNextProductInThePackage =
  (widgetId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const packageId = packageIdSelector(widgetId)(state)!;

    dispatch(changeProductAddStep({ id: widgetId, value: 'finish' }));

    dispatch(updatePackage(packageId, { status: 'loadingNextProduct' }));

    const currentProductIndexAtAdder = productPackageCurrentProductIndexSelector(packageId)(state)!;
    const nextIndex = findNextUnfilledProductIndexSelector(widgetId, packageId, currentProductIndexAtAdder!)(state);

    if (nextIndex >= 0) {
      dispatch(selectNextProduct(widgetId, nextIndex));
      dispatch(updatePackage(packageId, { status: 'loaded' }));
      return;
    }

    dispatch(updatePackage(packageId, { status: 'completed' }));
  };

const selectNextProduct =
  (widgetId: string, nextIndex: number) => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    const packageId = packageIdSelector(widgetId)(state)!;
    const { autoSelectProducts } = adderState(widgetId)(state);
    const productPackageProducts = productPackageProductsSelector(packageId)(state)!;

    if (autoSelectProducts) {
      dispatch(addNextProductPackageToAdderState(widgetId, productPackageProducts![nextIndex]!.productId));
    } else {
      dispatch(resetSelectedProduct(widgetId, ''));
    }
  };

export const addNextProductPackageToAdderState =
  (widgetId: string, productId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    const packageId = packageIdSelector(widgetId)(state)!;
    const productPackageProducts = productPackageProductsSelector(packageId)(state)!;

    const { productSelectionState } = adderState(widgetId)(state);

    if (productSelectionState[productId]?.currentProductAdderStep === 'finish') return;

    const productIndex = productPackageProducts!.findIndex((ppp) => ppp.productId === productId);

    dispatch(resetSelectedProduct(widgetId, productId));

    dispatch(loadProduct(productId));

    dispatch(updatePackage(packageId, { status: 'loaded', currentProductIndexAtAdder: productIndex }));
  };

export const resetSelectedProduct =
  (widgetId: string, productId = '') =>
  async (dispatch: AppDispatch) => {
    dispatch(updateAdderStateProductId({ id: widgetId, value: productId }));
  };

export const addPackageToCart = (widgetId: string) => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  const packageId = packageIdSelector(widgetId)(state)!;
  const { productSelectionState } = adderState(widgetId)(state);
  const productPackageProducts = productPackageProductsSelector(packageId)(state)!;

  const mapProductIdToProductPackageProductId = productPackageProducts.reduce((acc, ppp) => {
    acc[ppp.productId] = ppp.id;
    return acc;
  }, {} as Record<string, string>);

  const items = Object.keys(productSelectionState).map((productId) => {
    const adderResume = adderResumeSelector(widgetId, productId)(state);

    const item = createAddToCartItemRequestPayload({
      productId,
      selectedAdditionals: adderResume.selectedAdditionals,
      selectedResources: adderResume.selectedResources,
      selectedTariffs: adderResume.selectedTariffs,
      selectedDate: adderResume.selectedDate,
      selectedTariffHour: adderResume.selectedTariffHour,
    }) as AddCartItemRequest;

    return {
      item,
      productPackageProductId: mapProductIdToProductPackageProductId[productId]!,
    };
  });

  const payload: AddProductPackageToCartRequest = {
    items,
  };

  dispatch(addToCart(payload, widgetId));
};

export const toggleIsClosingAdderModal = (widgetId: string, isClosing: boolean) => (dispatch: AppDispatch) =>
  dispatch(updateIsClosingAdderModal(widgetId, isClosing));

export const closeAdderModal = (widgetId: string, packageId?: string) => async (dispatch: AppDispatch) => {
  if (packageId) {
    dispatch(resetAdderStateWithPackage(widgetId, packageId));
  }

  dispatch(toggleIsClosingAdderModal(widgetId, false));
  dispatch(clearHighlightedProductAdder());
};

const findNextUnfilledProductIndexSelector =
  (widgetId: string, packageId: string, index: number) => (state: RootState) => {
    const productPackageProducts = productPackageProductsSelector(packageId)(state) ?? [];
    const { productSelectionState } = adderState(widgetId)(state);

    return productPackageProducts!.findIndex(
      (ppp, idx) => productSelectionState[ppp.productId]?.currentProductAdderStep !== 'finish' && idx !== index,
    );
  };

export const packageProductPriceWithDiscountSelector =
  (widgetId: string, packageId: string, productId: string) => (state: RootState) => {
    const { tariffsPriceCents, additionalsPriceCents, resourcesPriceCents, totalCents } = adderResumeSelector(
      widgetId,
      productId,
    )(state);

    if (totalCents === 0) return totalCents;

    const productPackageProducts = productPackageProductsSelector(packageId)(state) ?? [];
    const productPackageProduct = productPackageProducts.find((ppp) => ppp.productId === productId);

    const discountPercentage = productPackageProduct?.discountPercentage ?? 0;

    if (!discountPercentage) return totalCents;

    const percentageValue = discountPercentage * tariffsPriceCents;
    const price = tariffsPriceCents - percentageValue;

    return price + additionalsPriceCents + resourcesPriceCents;
  };

export const productPackageTotalPriceSelector = (widgetId: string, packageId: string) => (state: RootState) => {
  const { productSelectionState } = adderState(widgetId)(state);

  const totalPrice = Object.entries(productSelectionState).reduce((acc, [id]) => {
    const currTotalCents = packageProductPriceWithDiscountSelector(widgetId, packageId, id)(state);

    acc += currTotalCents;

    return acc;
  }, 0);

  return totalPrice;
};

export const productPackageProductDiscountSelector =
  (id: string, productId: string, packageId = '') =>
  (state: RootState) => {
    if (!packageId) {
      return;
    }

    const productPackageProducts = productPackageProductsSelector(packageId)(state) ?? [];
    const productPackageProduct = productPackageProducts.find((ppp) => ppp.productId === productId);

    return productPackageProduct?.discountPercentage ?? 0;
  };

export const productPackageProductsSelector = (packageId: string) => (state: RootState) =>
  packageSelector(packageId)(state)?.productPackageProducts;

export const productPackageStatusSelector = (packageId: string) => (state: RootState) =>
  packageSelector(packageId)(state)?.status;

export const productPackageErrorSelector = (packageId: string) => (state: RootState) =>
  packageSelector(packageId)(state)?.error;

export const productPackageCurrentProductIndexSelector = (packageId: string) => (state: RootState) =>
  packageSelector(packageId)(state)?.currentProductIndexAtAdder;

export const productPackageSelector = (packageId: string) => (state: RootState) =>
  packageSelector(packageId)(state)?.productPackage;

export const checkIfThePackageHasInactiveProductSelector = (packageId: string) => (state: RootState) => {
  const productPackageProducts = productPackageProductsSelector(packageId)(state) ?? [];

  return productPackageProducts.some((productPackageProducts) => !productPackageProducts.product!.active);
};

export const isClosingAdderModalSelector = (widgetId: string) => (state: RootState) =>
  adderState(widgetId)(state).isClosingAdderModal;

export const packageIdSelector = (widgetId: string) => (state: RootState) =>
  adderState(widgetId)(state).packageId || '';

export const isProductPackage = (widgetId: string) => (state: RootState) => packageIdSelector(widgetId)(state) !== '';

export const adderStateAndCurrentProductIdSelector = (widgetId: string) => (state: RootState) => {
  const adder = adderState(widgetId)(state);
  const productId = adder.productId;

  return { adderState: adder, productId };
};

export const productSelectionStateSelector = (widgetId: string) => (state: RootState) =>
  adderState(widgetId)(state).productSelectionState;
